



















































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums
import { DataModules } from "@/store/modules/data";

// Types
import { IVaultAsset, IVaultAssetForm } from "@/models/vaultAssets";
import { IClient } from "@/models/clients";
import { IAccount } from "@/models/accounts";
import { IContractProduct } from "@/models/contracts";
import { ILead } from "@/models/leads";

@Component({
  components: {
    "select-client-contract-product": () =>
      import("@/components/app/global/fields/selectClientContractProduct.vue")
  }
})
export default class UpdateVaultAssetModal extends Mixins(
  RequestPromises,
  HasValidation,
  Context
) {
  public declare $refs: {
    noteField: HTMLFormElement;
    labelField: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Props

  @Prop({ required: true })
  public asset!: IVaultAsset;

  @Prop({ default: null })
  public clientId!: IClient["id"];

  @Prop({ default: null })
  public accountId!: IAccount["id"];

  @Prop({ default: null })
  public leadId!: ILead["id"];

  @Prop({ default: null })
  public contractProduct!: IContractProduct;

  @Prop({ default: "" })
  public note!: string;

  @Prop({ default: false })
  public isConvertingToSecret!: boolean;

  // Data

  public isLoading = true;
  public isReloading = true;
  public hasRequestError = false;
  public isProcessing = false;
  public isDisabled = false;

  public form = {} as IVaultAssetForm;
  public decryptedNote = "";

  public product =
    this.contractProduct ||
    (this.asset.contract_product
      ? { ...this.asset.contract_product, client_id: this.clientId }
      : {});

  // Life-cycle methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  // Methods

  public async loadData() {
    try {
      if (
        this.asset.encrypted &&
        this.canDecrypt &&
        !this.isConvertingToSecret
      ) {
        const { note } = await this.decryptAssetNote();
        this.decryptedNote = note;
      }

      this.form = this.initForm();
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isReloading = false;
      this.isLoading = false;

      this.$nextTick(() => {
        if (!this.isConvertingToSecret) {
          if (!this.$_.has(this.$refs, "noteField")) return;
          this.$refs.noteField.$el.focus();
          return;
        }
        if (!this.$_.has(this.$refs, "labelField")) return;
        this.$refs.labelField.focus();
      });
    }
  }

  public async reloadData() {
    this.isReloading = true;
    await this.loadData();
  }

  public async decryptAssetNote() {
    return this.$store.dispatch(`data/${this.dataModule}/decrypt`, {
      id: this.ownerId,
      assetId: this.asset.id
    });
  }

  public initForm(): IVaultAssetForm {
    return _.merge(
      _.pick(this.asset, [
        "contract_product_id",
        "label",
        "visible_for_client",
        "encrypted"
      ]),
      {
        note:
          this.asset.encrypted && this.canDecrypt && !this.isConvertingToSecret
            ? this.decryptedNote
            : this.asset.note
      }
    );
  }

  public async validateBeforeSubmit() {
    if (!this.canUpdate) return;

    try {
      this.isProcessing = true;
      await this.updateVaultAsset();
      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateVaultAsset() {
    return this.$store.dispatch(`data/${this.dataModule}/update`, {
      id: this.ownerId,
      assetId: this.asset.id,
      data: this.form
    });
  }

  public onNoteChange(value) {
    if (!this.canDecrypt) return;
    this.$set(this.form, "note", value);
  }

  // Computed

  public get isSecret() {
    return this.asset.encrypted;
  }

  public get isClientVault() {
    return _.isNull(this.leadId);
  }

  public get dataModule() {
    return this.isClientVault
      ? DataModules.CLIENTS_VAULT_ASSETS
      : DataModules.LEADS_VAULT_ASSETS;
  }

  public get ownerId() {
    return this.isClientVault ? this.clientId : this.leadId;
  }

  public get title() {
    if (this.isSecret) return this.$t("_action.edit_secret");
    return this.$t("_action.edit_note");
  }

  public get formIsValid() {
    return !this.$_.isEqual(this.form, this.initForm()) && !this.hasErrors;
  }

  public get formIsDisabled() {
    return this.isProcessing || !this.formIsValid;
  }

  // Guarding

  public get canUpdate() {
    return this.isClientVault
      ? this.$userCan("update_client_vault_asset")
      : this.$userCan("update_lead_vault_asset");
  }

  public get canDecrypt() {
    return this.isClientVault
      ? this.$userCan("decrypt_client_vault_asset")
      : this.$userCan("decrypt_lead_vault_asset");
  }

  public guardFunctionalities = this.isClientVault
    ? ["get_client_vault_asset"]
    : ["get_lead_vault_asset"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
